/**
 * Core API Client
 *
 * Routes all requests to the Render core-api service via VITE_CORE_API_URL.
 */

import { sbFromEnv } from './supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
const supabaseAnon = import.meta.env.VITE_SUPABASE_ANON_KEY!;

const CORE_API_URL = import.meta.env.VITE_CORE_API_URL || '';

const supabase = sbFromEnv({
  url: supabaseUrl,
  key: supabaseAnon,
});

interface CoreApiOptions extends Omit<RequestInit, 'body'> {
  body?: Record<string, unknown>;
}

/**
 * Make a request to the core API
 */
export async function coreApi<T = unknown>(
  path: string,
  options: CoreApiOptions = {}
): Promise<T> {
  // Get fresh session token
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const headers = new Headers(options.headers || {});
  headers.set('Content-Type', 'application/json');
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const url = `${CORE_API_URL}${path}`;

  const res = await fetch(url, {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }

  if (!res.ok) {
    const errorBody = body as { error?: { message?: string } | string } | null;
    let msg = `HTTP ${res.status}`;
    if (errorBody?.error) {
      msg = typeof errorBody.error === 'string'
        ? errorBody.error
        : errorBody.error.message || msg;
    }
    throw new Error(msg);
  }

  return body as T;
}

// ============ Auth APIs ============

export interface AuthSession {
  user: {
    id: string;
    email: string | null;
  };
  profile: {
    id: string;
    role: string | null;
    display_name: string | null;
    email: string | null;
    avatar_url: string | null;
    permissions: Record<string, unknown> | null;
  } | null;
}

/**
 * Get current auth session with profile
 */
export async function getAuthSession(): Promise<{ data: AuthSession; error: null }> {
  return coreApi('/api/v1/auth/session');
}

// ============ Students APIs ============

export interface Student {
  id: string;
  full_name: string | null;
  display_name: string | null;
  grade: string | null;
  classroom_id: string | null;
  created_at: string;
}

/**
 * List students in a classroom
 */
export async function listStudents(classroomId: string): Promise<{ data: { students: Student[] }; error: null }> {
  return coreApi(`/api/v1/students?classroom_id=${classroomId}`);
}

/**
 * Get a single student
 */
export async function getStudent(studentId: string): Promise<{ data: Student; error: null }> {
  return coreApi(`/api/v1/students/${studentId}`);
}

/**
 * Create a new student
 */
export async function createStudent(params: {
  classroom_id: string;
  full_name: string;
  display_name?: string;
  grade?: string;
}): Promise<{ data: Student; error: null }> {
  return coreApi('/api/v1/students', { method: 'POST', body: params });
}

/**
 * Update a student
 */
export async function updateStudent(studentId: string, params: {
  full_name?: string;
  display_name?: string;
  grade?: string | null;
}): Promise<{ data: Student; error: null }> {
  return coreApi(`/api/v1/students/${studentId}`, { method: 'PATCH', body: params });
}

/**
 * Delete a student
 */
export async function deleteStudent(studentId: string): Promise<{ data: { deleted: boolean }; error: null }> {
  return coreApi(`/api/v1/students/${studentId}`, { method: 'DELETE' });
}

// ============ Classrooms APIs ============

export interface Classroom {
  id: string;
  name: string | null;
  public_name: string | null;
  grade_level: string | null;
  share_code: string | null;
  teacher_id: string | null;
  storybooks_enabled: boolean;
  archived_at: string | null;
  created_at: string;
}

/**
 * List classrooms for current teacher
 */
export async function listClassrooms(): Promise<{ data: { classrooms: Classroom[] }; error: null }> {
  return coreApi('/api/v1/classrooms');
}

/**
 * Get a single classroom
 */
export async function getClassroom(classroomId: string): Promise<{ data: Classroom; error: null }> {
  return coreApi(`/api/v1/classrooms/${classroomId}`);
}

/**
 * Create a new classroom
 */
export async function createClassroom(params: {
  name: string;
  public_name?: string;
  grade_level?: string;
}): Promise<{ data: Classroom; error: null }> {
  return coreApi('/api/v1/classrooms', { method: 'POST', body: params });
}

/**
 * Update a classroom
 */
export async function updateClassroom(classroomId: string, params: {
  name?: string;
  public_name?: string;
  grade_level?: string | null;
  storybooks_enabled?: boolean;
}): Promise<{ data: Classroom; error: null }> {
  return coreApi(`/api/v1/classrooms/${classroomId}`, { method: 'PATCH', body: params });
}

/**
 * Get classroom roster (students)
 */
export async function getClassroomRoster(classroomId: string): Promise<{ data: { roster: Student[] }; error: null }> {
  return coreApi(`/api/v1/classrooms/${classroomId}/roster`);
}

// ============ Messages APIs ============

export interface MessageThread {
  thread_id: string;
  subject: string | null;
  student_id: string | null;
  student_name: string;
  last_message_excerpt: string;
  last_message_at: string;
  unread_count: number;
}

export interface Message {
  id: string;
  author_id: string | null;
  body: string | null;
  sender_role: string | null;
  read_at: string | null;
  created_at: string;
}

/**
 * List message threads for current user
 */
export async function listMessageThreads(): Promise<{ data: { threads: MessageThread[] }; error: null }> {
  return coreApi('/api/v1/messages/threads');
}

/**
 * Get messages in a thread
 */
export async function getMessageThread(threadId: string): Promise<{ data: { thread: { id: string; subject: string | null }; messages: Message[] }; error: null }> {
  return coreApi(`/api/v1/messages/threads/${threadId}`);
}

/**
 * Send a message (new thread or reply)
 */
export async function sendMessage(params: {
  thread_id?: string;
  student_id?: string;
  guardian_id?: string;
  body: string;
}): Promise<{ data: { success: boolean; thread_id: string; message: Message }; error: null }> {
  return coreApi('/api/v1/messages/send', { method: 'POST', body: params });
}

/**
 * Mark thread messages as read
 */
export async function markThreadRead(threadId: string): Promise<{ data: { success: boolean }; error: null }> {
  return coreApi(`/api/v1/messages/threads/${threadId}/read`, { method: 'POST' });
}

// ============ Credits APIs ============

export interface CreditBalance {
  balance_credits: number;
  balance_cents: number;
  balance_usd: string;
}

export interface LedgerEntry {
  id: string;
  delta_cents: number;
  delta_credits: number;
  type: string | null;
  source: string | null;
  note: string | null;
  created_at: string;
}

/**
 * Get current user's credit balance
 */
export async function getCreditBalance(): Promise<{ data: CreditBalance; error: null }> {
  return coreApi('/api/v1/credits/balance');
}

/**
 * Get credit transaction history
 */
export async function getCreditHistory(params?: {
  limit?: number;
  offset?: number;
}): Promise<{ data: { entries: LedgerEntry[]; total: number }; error: null }> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString();

  return coreApi(`/api/v1/credits/history${qs ? `?${qs}` : ''}`);
}

// ============ Tickets APIs ============

export interface Ticket {
  id: string;
  subject: string | null;
  status: string | null;
  priority: string | null;
  category: string | null;
  created_by: string | null;
  assignee_id: string | null;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown> | null;
}

export interface TicketMessage {
  id: string;
  author_id: string | null;
  body: string | null;
  sender_role: string | null;
  visibility: string | null;
  created_at: string;
}

/**
 * List tickets for current user
 */
export async function listTickets(params?: {
  page?: number;
  page_size?: number;
  status?: string;
  category?: string;
  priority?: string;
  scope?: 'self' | 'admin';
  q?: string;
  assignee_id?: string;
  source?: string;
  created_since?: string;
}): Promise<{ data: { items: Ticket[]; total: number; page: number; pageSize: number }; error: null }> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.page_size) query.set('page_size', String(params.page_size));
  if (params?.status) query.set('status', params.status);
  if (params?.category) query.set('category', params.category);
  if (params?.priority) query.set('priority', params.priority);
  if (params?.scope) query.set('scope', params.scope);
  if (params?.q) query.set('q', params.q);
  if (params?.assignee_id) query.set('assignee_id', params.assignee_id);
  if (params?.source) query.set('source', params.source);
  if (params?.created_since) query.set('created_since', params.created_since);
  const qs = query.toString();

  return coreApi(`/api/v1/tickets${qs ? `?${qs}` : ''}`);
}

/**
 * Get a ticket with messages
 */
export async function getTicket(ticketId: string): Promise<{ data: { ticket: Ticket; messages: TicketMessage[] }; error: null }> {
  return coreApi(`/api/v1/tickets/${ticketId}`);
}

/**
 * Create a new ticket
 */
export async function createTicket(params: {
  subject: string;
  message: string;
  category: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  metadata?: Record<string, unknown>;
}): Promise<{ data: { ticket: Ticket; message: TicketMessage }; error: null }> {
  return coreApi('/api/v1/tickets', { method: 'POST', body: params });
}

/**
 * Add a message to a ticket
 */
export async function addTicketMessage(ticketId: string, params: {
  body: string;
  visibility?: 'public' | 'internal';
}): Promise<{ data: TicketMessage; error: null }> {
  return coreApi(`/api/v1/tickets/${ticketId}/messages`, { method: 'POST', body: params });
}

/**
 * Close a ticket
 */
export async function closeTicket(ticketId: string, notes?: string): Promise<{ data: Ticket; error: null }> {
  return coreApi(`/api/v1/tickets/${ticketId}/close`, { method: 'POST', body: { resolved_notes: notes } });
}

/**
 * Update a ticket (admin only - status, priority, assignee)
 */
export async function updateTicket(ticketId: string, params: {
  status?: string;
  priority?: string;
  assignee_id?: string | null;
}): Promise<{ data: Ticket; error: null }> {
  return coreApi(`/api/v1/tickets/${ticketId}`, { method: 'PATCH', body: params });
}

// ============ Sponsors APIs ============

export interface Sponsor {
  id: string;
  handle: string;
  display_name: string;
  tagline: string | null;
  website_url: string | null;
  logo_url: string | null;
  avatar_url: string | null;
  bio: string | null;
  location: string | null;
  is_public: boolean;
  created_at: string;
}

/**
 * Get current sponsor's profile
 */
export async function getSponsorProfile(): Promise<{ data: Sponsor; error: null }> {
  return coreApi('/api/v1/sponsors/me');
}

/**
 * Get a public sponsor by handle
 */
export async function getPublicSponsor(handle: string): Promise<{ data: Sponsor; error: null }> {
  return coreApi(`/api/v1/sponsors/${handle}`);
}

/**
 * List public sponsors
 */
export async function listPublicSponsors(params?: {
  limit?: number;
  offset?: number;
}): Promise<{ data: { sponsors: Sponsor[]; total: number }; error: null }> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString();

  return coreApi(`/api/v1/sponsors${qs ? `?${qs}` : ''}`);
}

/**
 * Update sponsor profile
 */
export async function updateSponsorProfile(params: {
  handle?: string;
  display_name?: string;
  tagline?: string | null;
  website_url?: string | null;
  bio?: string | null;
  is_public?: boolean;
}): Promise<{ data: Sponsor; error: null }> {
  return coreApi('/api/v1/sponsors/me', { method: 'PATCH', body: params });
}

/**
 * Get sponsor wallet balance + recent ledger
 */
export async function getSponsorWallet(): Promise<{ data: { account: { id: string | null; balance_credits: number }; wallet: { accountId: string | null; balanceCredits: number; account: Record<string, unknown> | null }; ledger: unknown[] }; error: null }> {
  return coreApi('/api/v1/sponsors/wallet');
}

/**
 * List active credit packs
 */
export async function getSponsorPacks(): Promise<{ data: { items: CreditPack[] }; error: null }> {
  return coreApi('/api/v1/sponsors/packs');
}

/**
 * Check if a sponsor handle is available
 */
export async function checkSponsorHandleAvailable(handle: string): Promise<{ available: boolean; handle: string }> {
  return coreApi(`/api/v1/sponsors/handle-available?handle=${encodeURIComponent(handle)}`);
}

/**
 * Get sponsor dashboard summary
 */
export async function getSponsorDashboardSummary(sponsorUserId?: string): Promise<{ data: Record<string, unknown>; error: null }> {
  const qs = sponsorUserId ? `?sponsor_user_id=${sponsorUserId}` : '';
  return coreApi(`/api/v1/sponsors/dashboard-summary${qs}`);
}

/**
 * Gift credits to a teacher via share code
 */
export async function giftSponsorCredits(params: {
  teacher_share_code: string;
  credits: number;
}): Promise<{ data: { ok: boolean; sponsor_balance: number; teacher_balance: number; note: string }; error: null }> {
  return coreApi('/api/v1/sponsors/gift-credits', { method: 'POST', body: params });
}

/**
 * Initialize a sponsor by email
 */
export async function startSponsor(params: {
  email: string;
  display_name?: string;
}): Promise<{ ok: boolean; id: string }> {
  return coreApi('/api/v1/sponsors/start', { method: 'POST', body: params });
}

/**
 * Get sponsor inbox (shares, gifts, balance)
 */
export async function getSponsorInbox(sponsorId: string): Promise<{ data: { shares: unknown[]; gifts: unknown[]; balance_cents: number }; error: null }> {
  return coreApi(`/api/v1/sponsors/inbox?sponsor_id=${sponsorId}`);
}

/**
 * Update a share status
 */
export async function updateSponsorShare(params: {
  id: string;
  status: 'unread' | 'read' | 'archived';
}): Promise<{ data: Record<string, unknown>; error: null }> {
  return coreApi('/api/v1/sponsors/share-update', { method: 'POST', body: params });
}

/**
 * Update sponsor public profile (legacy path - wraps PATCH /me)
 */
export async function updateSponsorPublicProfile(params: Record<string, unknown>): Promise<{ data: { sponsor: Record<string, unknown> }; error: null }> {
  return coreApi('/api/v1/sponsors/me', { method: 'PATCH', body: params });
}

/**
 * Sponsor checkout (Render core-api)
 */
export async function sponsorCheckout(params: {
  pack_key?: string;
  pack_id?: string;
  price_id?: string;
  success_url?: string;
  cancel_url?: string;
}): Promise<{ data: { url: string }; error: null }> {
  return coreApi('/api/v1/checkout/sponsor', { method: 'POST', body: params });
}

// ============ Marketplace APIs ============

export interface MarketItem {
  id: string;
  sku: string;
  title: string;
  description: string | null;
  previewUrl: string | null;
  priceCredits: number | null;
  priceCents: number | null;
  qtyLimitPerUser: number | null;
  kind: string;
  meta: Record<string, unknown>;
}

export interface CatalogSection {
  badges: MarketItem[];
  avatars: MarketItem[];
  themes: MarketItem[];
  borders: MarketItem[];
  digital_goods: MarketItem[];
  special_offers: MarketItem[];
  ai_tools: MarketItem[];
}

export interface CreditPack {
  id: string;
  name: string;
  usd_price: number;
  credit_value_cents: number;
  active: boolean;
}

export interface OwnedItem {
  inventory_id: string;
  item_id: string;
  quantity: number;
  acquired_at: string;
  item: {
    id: string;
    sku: string;
    title: string;
    description: string | null;
    preview_url: string | null;
    kind: string;
    meta: Record<string, unknown>;
  };
}

/**
 * Get marketplace catalog with role-based pricing
 */
export async function getMarketplaceCatalog(role?: string): Promise<{ data: { role: string; sections: CatalogSection; items: unknown[]; packs: CreditPack[] }; error: null }> {
  const qs = role ? `?role=${role}` : '';
  return coreApi(`/api/v1/marketplace/catalog${qs}`);
}

/**
 * Get items owned by current user
 */
export async function getOwnedItems(): Promise<{ data: OwnedItem[]; error: null }> {
  return coreApi('/api/v1/marketplace/owned');
}

/**
 * Purchase an item with credits
 */
export async function purchaseItem(params: {
  item_id: string;
  quantity?: number;
}): Promise<{ data: { ok: boolean; item_id: string; quantity: number; total_cost: number; new_balance: number }; error: null }> {
  return coreApi('/api/v1/marketplace/purchase', { method: 'POST', body: params });
}

// ============ Nominations APIs ============

export interface TeacherNomination {
  id: string;
  nominator_name: string;
  nominator_email: string;
  teacher_name: string;
  teacher_email: string;
  school: string;
  reason: string | null;
  status: string;
  created_at: string;
  confirmed_at: string | null;
}

export interface ClassroomNomination {
  id: string;
  nominator_name: string;
  nominator_email: string;
  classroom_name: string;
  teacher_name: string;
  teacher_email: string;
  school: string;
  grade_level: string | null;
  reason: string | null;
  status: string;
  created_at: string;
  confirmed_at: string | null;
}

/**
 * Submit a teacher nomination (public, no auth)
 */
export async function nominateTeacher(params: {
  nominator_name: string;
  nominator_email: string;
  teacher_name: string;
  teacher_email: string;
  school: string;
  reason?: string;
}): Promise<{ data: { ok: boolean }; error: null }> {
  return coreApi('/api/v1/nominations/teacher', { method: 'POST', body: params });
}

/**
 * Submit a classroom nomination (public, no auth)
 */
export async function nominateClassroom(params: {
  nominator_name: string;
  nominator_email: string;
  classroom_name: string;
  teacher_name: string;
  teacher_email: string;
  school: string;
  grade_level?: string;
  reason?: string;
}): Promise<{ data: { ok: boolean }; error: null }> {
  return coreApi('/api/v1/nominations/classroom', { method: 'POST', body: params });
}

/**
 * Verify a nomination token
 */
export async function verifyNominationToken(token: string, type: 'teacher' | 'classroom' = 'teacher'): Promise<{ data: { type: string; nomination: TeacherNomination | ClassroomNomination }; error: null }> {
  return coreApi(`/api/v1/nominations/verify?token=${token}&type=${type}`);
}

/**
 * Confirm a nomination
 */
export async function confirmNomination(token: string, type: 'teacher' | 'classroom' = 'teacher'): Promise<{ data: { ok: boolean; already_confirmed?: boolean }; error: null }> {
  return coreApi('/api/v1/nominations/confirm', { method: 'POST', body: { token, type } });
}

// ============ Community APIs ============

export interface CommunityClassroom {
  classroom_id: string;
  classroom_name: string;
  display_name: string;
  grade_level: string | null;
  teacher_name: string;
  teacher_avatar: string | null;
  school: string | null;
  city: string | null;
  state: string | null;
  share_code: string | null;
  about: string | null;
  theme_style: string;
  accent_color: string;
  updated_at: string;
}

export interface CommunitySponsor {
  id: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  joined_at?: string;
}

export interface CommunityGift {
  id: string;
  amount: number;
  message: string | null;
  created_at: string;
}

/**
 * Get public community classrooms (for rotation/showcase)
 */
export async function getCommunityClassrooms(params?: {
  limit?: number;
  offset?: number;
}): Promise<{ data: { items: CommunityClassroom[]; total: number; limit: number; offset: number }; error: null }> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString();

  return coreApi(`/api/v1/community/classrooms${qs ? `?${qs}` : ''}`);
}

/**
 * Get public community sponsors
 */
export async function getCommunitySponsors(params?: {
  limit?: number;
  offset?: number;
}): Promise<{ data: { items: CommunitySponsor[]; total: number; limit: number; offset: number }; error: null }> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString();

  return coreApi(`/api/v1/community/sponsors${qs ? `?${qs}` : ''}`);
}

/**
 * Get random featured sponsors
 */
export async function getRandomSponsors(count = 3): Promise<{ data: CommunitySponsor[]; error: null }> {
  return coreApi(`/api/v1/community/sponsors/random?count=${count}`);
}

/**
 * Get recent community gift activity
 */
export async function getCommunityGifts(limit = 10): Promise<{ data: CommunityGift[]; error: null }> {
  return coreApi(`/api/v1/community/gifts?limit=${limit}`);
}

// ============ Coach APIs ============

export interface CoachWalletData {
  wallet: {
    accountId: string | null;
    balanceCredits: number;
    account: Record<string, unknown> | null;
  };
}

export async function getCoachWallet(): Promise<{ data: CoachWalletData; error: null }> {
  return coreApi('/api/v1/coach/wallet');
}

export async function getCoachBalance(): Promise<{ data: { balance_credits: number }; error: null }> {
  return coreApi('/api/v1/coach/balance');
}

export async function getCoachPacks(): Promise<{ data: { items: CreditPack[] }; error: null }> {
  return coreApi('/api/v1/coach/packs');
}

export async function coachCheckout(packName: string): Promise<{ data: { url: string }; error: null }> {
  return coreApi('/api/v1/coach/checkout', {
    method: 'POST',
    body: { pack: packName },
  });
}

export interface CoachMarketBalances {
  coach_balance_credits: number;
  locker_room: {
    id: string;
    name: string;
    balance_credits: number;
  } | null;
}

export async function getCoachMarketBalances(): Promise<{ data: CoachMarketBalances; error: null }> {
  return coreApi('/api/v1/coach/market-balances');
}

export async function coachMarketPurchase(params: {
  item_id: string;
  spend_source?: string;
  locker_room_id?: string | null;
}): Promise<{ data: { ok: boolean }; error: null }> {
  return coreApi('/api/v1/coach/market-purchase', { method: 'POST', body: params });
}

export async function coachTransferCredits(params: {
  prospectUserId?: string;
  prospect_user_id?: string;
  amount?: number;
  amount_credits?: number;
  note?: string;
  [key: string]: unknown;
}): Promise<{ data: { ok: boolean }; error: null }> {
  return coreApi('/api/v1/coach/transfer-credits', { method: 'POST', body: params });
}

export async function coachAcceptProspect(token: string): Promise<{
  data: { ok: boolean; prospect_id: number; locker_room_id: string; nomination_code: string | null };
  error: null;
}> {
  return coreApi('/api/v1/coach/accept-prospect', { method: 'POST', body: { token } });
}

// ============ Locker Room APIs ============

export async function getLockerRoomContext(): Promise<{ data: { role: string; coachProfile: Record<string, unknown> | null; prospects: unknown[] }; error: null }> {
  return coreApi('/api/v1/locker-room/context');
}

export async function getLockerRoomBudgetState(lockerRoomId: string): Promise<{ data: Record<string, unknown>; error: null }> {
  return coreApi(`/api/v1/locker-room/budget-state?locker_room_id=${lockerRoomId}`);
}

export async function saveLockerRoomBudget(params: {
  lockerRoomId: string;
  rows: Array<{ prospectId: number; plannedAnnualCents: number; plannedMonthlyCents: number }>;
}): Promise<{ data: { ok: boolean; count: number }; error: null }> {
  return coreApi('/api/v1/locker-room/budget-save', { method: 'POST', body: params });
}

export async function getLockerRoomDonationShare(): Promise<{ data: { ok: boolean; code: string; status: string; url: string }; error: null }> {
  return coreApi('/api/v1/locker-room/donation-share');
}

export async function updateLockerRoomDonationShare(params: {
  rotate?: boolean;
  archive?: boolean;
  force?: boolean;
  locker_room_id?: string;
}): Promise<{ data: { ok: boolean; code: string; status: string; url: string; updated_at?: string; rotated?: boolean }; error: null }> {
  return coreApi('/api/v1/locker-room/donation-share', { method: 'POST', body: params });
}

export async function revokeLockerRoomShare(params: {
  code: string;
  prospect_id?: number;
}): Promise<{ data: { ok: boolean }; error: null }> {
  return coreApi('/api/v1/locker-room/share-revoke', { method: 'POST', body: params });
}

export async function emitLockerRoomConfigLedger(params: Record<string, unknown>): Promise<{ data: { ok: boolean }; error: null }> {
  return coreApi('/api/v1/locker-room/config-ledger', { method: 'POST', body: params });
}

export async function allocateLockerRoomCredits(params: {
  locker_room_id: string;
  allocations: Array<{ prospect_id: number; credits: number }>;
}): Promise<{ data: { ok: boolean; allocated: number; recipients: number; new_pool_balance: number }; error: null }> {
  return coreApi('/api/v1/locker-room/credit-allocate', { method: 'POST', body: params });
}

export async function provisionLockerRoom(params: {
  email: string;
  role?: string;
  full_name?: string;
  coach_id?: string;
  coach_email?: string;
  send_magic_link?: boolean;
  status?: string;
}): Promise<{ data: { ok: boolean; role: string; userId: string; prospectId: number | null; coachId: string | null; action_link: string | null }; error: null }> {
  return coreApi('/api/v1/locker-room/provision', { method: 'POST', body: params });
}

export async function getLockerRoomInvite(code: string): Promise<{ data: { prospect: Record<string, unknown>; lockerRoom: Record<string, unknown> }; error: null }> {
  return coreApi(`/api/v1/locker-room/invite?code=${encodeURIComponent(code)}`);
}

export async function getLockerDonateLink(code: string): Promise<{ data: { code: string; locker_room: Record<string, unknown> }; error: null }> {
  return coreApi(`/api/v1/locker-room/donate-link?code=${encodeURIComponent(code)}`);
}

export async function getLockerRoomPublic(slug: string): Promise<{ data: { locker_room: Record<string, unknown> }; error: null }> {
  return coreApi(`/api/v1/locker-room/public?slug=${encodeURIComponent(slug)}`);
}

export async function sponsorGiftLockerRoom(params: {
  locker_room_id: string;
  amount_credits: number;
}): Promise<{ data: { ok: boolean; locker_room_id: string; amount_credits: number; ledger_id: string }; error: null }> {
  return coreApi('/api/v1/locker-room/sponsor-gift', { method: 'POST', body: params });
}

// ============ Prospect Shopping APIs ============

export async function startProspectShopping(params: {
  prospect_id: number;
  estimated_budget_cents?: number;
}): Promise<{ session: Record<string, unknown> }> {
  return coreApi('/api/v1/locker-room/prospect-shopping-start', { method: 'POST', body: params });
}

// ============ Teacher APIs ============

export interface TeacherBudget {
  program_id: string | null;
  available_cents: number;
  updated_at: string;
}

export interface TeacherBalanceSnapshot {
  teacher_id: string;
  total_available_cents: number;
  budgets: TeacherBudget[];
}

/**
 * Get teacher budget balance snapshot
 */
export async function getTeacherBalance(teacherId?: string): Promise<{ data: TeacherBalanceSnapshot; error: null }> {
  const qs = teacherId ? `?teacherId=${teacherId}` : '';
  return coreApi(`/api/v1/teacher/balance${qs}`);
}

/**
 * Bootstrap teacher (ensure classroom + credits exist)
 */
export async function teacherBootstrap(): Promise<{ data: { ok: boolean; classroomId: string }; error: null }> {
  return coreApi('/api/v1/teacher/bootstrap', { method: 'POST' });
}

/**
 * Create a teacher share link
 */
export async function createTeacherShare(params?: {
  teacher_id?: string;
  note?: string;
}): Promise<{ data: { ok: boolean; code: string; url: string }; error: null }> {
  return coreApi('/api/v1/teacher/shares', { method: 'POST', body: params || {} });
}

/**
 * Revoke a teacher share link
 */
export async function revokeTeacherShare(params: {
  code: string;
  teacher_id?: string;
}): Promise<{ data: { ok: boolean }; error: null }> {
  return coreApi('/api/v1/teacher/shares/revoke', { method: 'POST', body: params });
}

/**
 * Get KYC verification status
 */
export async function getKycStatus(userId?: string): Promise<{ data: { status: string }; error: null }> {
  const qs = userId ? `?user_id=${userId}` : '';
  return coreApi(`/api/v1/teacher/kyc-status${qs}`);
}

/**
 * Admin: update KYC application status
 */
export async function updateKycStatus(params: {
  user_id: string;
  status: string;
}): Promise<{ data: { status: string }; error: null }> {
  return coreApi('/api/v1/teacher/kyc-status', { method: 'POST', body: params });
}

export interface ParentUpdate {
  student_id: string;
  student_name: string;
  classroom_id: string;
  updates: Array<{
    id: string;
    created_at: string;
    kind: string;
    note: string;
  }>;
}

/**
 * Get parent daily updates feed for teacher's students
 */
export async function getTeacherParentUpdates(): Promise<{ data: ParentUpdate[]; error: null }> {
  return coreApi('/api/v1/teacher/parent-updates');
}

export interface IssuedCard {
  pan: string;
  cvc: string;
  exp_month: number;
  exp_year: number;
  last4: string;
}

/**
 * Issue a single-use virtual card for shopping
 */
export async function issueCard(params: {
  merchant: string;
  amountCents: number;
  programId?: string;
}): Promise<{ data: { ok: boolean; provider: string; card: IssuedCard }; error: null }> {
  return coreApi('/api/v1/teacher/cards/issue', { method: 'POST', body: params });
}

// ============ User Profiles APIs ============

export interface UserProfile {
  user_id: string;
  handle: string | null;
  display_name: string | null;
  tagline: string | null;
  avatar_url: string | null;
  cover_url: string | null;
  bio_md: string | null;
  location: string | null;
  theme: string | null;
  quote_text: string | null;
  quote_attr: string | null;
  links: unknown[] | null;
  feed_entries: unknown[] | null;
  badges: unknown[] | null;
  visibility: string | null;
  verified_at: string | null;
  welcomed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  role?: string | null;
}

export async function getUserProfile(): Promise<{ data: UserProfile }> {
  return coreApi('/api/v1/user-profiles/me');
}

export async function updateUserProfile(params: Record<string, unknown>): Promise<{ data: UserProfile }> {
  return coreApi('/api/v1/user-profiles/me', { method: 'PATCH', body: params });
}

export async function checkUserHandleAvailable(handle: string): Promise<{ available: boolean; handle: string }> {
  return coreApi(`/api/v1/user-profiles/handle-available?h=${encodeURIComponent(handle)}`);
}

export async function getPublicUserProfile(handle: string): Promise<{ data: UserProfile }> {
  return coreApi(`/api/v1/user-profiles/by-handle/${encodeURIComponent(handle)}`);
}

export async function markWelcomeSeen(): Promise<{ data: { ok: boolean; welcomed_at: string } }> {
  return coreApi('/api/v1/user-profiles/me/welcomed', { method: 'POST' });
}

export async function getUserProfileById(userId: string): Promise<{ data: UserProfile }> {
  return coreApi(`/api/v1/user-profiles/by-id/${encodeURIComponent(userId)}`);
}

// ============ Utility ============

/**
 * Get the current core API base URL
 */
export function getCoreApiUrl(): string {
  return CORE_API_URL || window.location.origin;
}
